import { pickTodayOptimalDay } from './favoriteStandForecastUtils';
import { buildDetailScoreFactors } from './forecastScoreFactors';
import { formatLocalDate, toLocalYMD } from '../../utils/dateUtils';
import { getCardinalFromDegrees } from '../map/utils/windUtils';

/** True when backend attached at least one useful weather field for a day. */
export function dayHasWeather(weather) {
  if (!weather || typeof weather !== 'object') return false;
  return weather.temp_max != null
    || weather.temp_day != null
    || weather.wind_speed != null
    || weather.pressure != null
    || weather.weather_type != null;
}

/** Score band label aligned with hunt-forecast mockup. */
export function getForecastScoreLabel(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return { label: '—', tone: 'muted' };
  if (n >= 85) return { label: 'PRIME', tone: 'good' };
  if (n >= 75) return { label: 'EXCELLENT', tone: 'good' };
  if (n >= 60) return { label: 'GOOD', tone: 'good' };
  if (n >= 45) return { label: 'FAIR', tone: 'fair' };
  return { label: 'POOR', tone: 'poor' };
}

export function toForecastCardinal(deg) {
  if (deg == null) return '';
  try {
    return getCardinalFromDegrees(Number(deg));
  } catch {
    return '';
  }
}

export function formatForecastTime(iso, timeZone) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timeZone || undefined,
    });
  } catch {
    return '—';
  }
}

export function formatMajorWindow(pick) {
  const tz = pick?.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const block = pick?.day?.solunar?.majors?.[0];
  if (!block) return 'Majors unavailable';
  return `${formatForecastTime(block.start, tz)} – ${formatForecastTime(block.end, tz)}`;
}

export function formatMinorWindow(pick) {
  const tz = pick?.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const block = pick?.day?.solunar?.minors?.[0];
  if (!block) return '—';
  return `${formatForecastTime(block.start, tz)} – ${formatForecastTime(block.end, tz)}`;
}

/** Enamel band pill class for score label (thresholds unchanged). */
export function getForecastScoreBandClass(score) {
  const { label } = getForecastScoreLabel(score);
  if (!label || label === '—') return 'hf-band-pill--muted';
  return `hf-band-pill--${label.toLowerCase()}`;
}

/** PRESSURE / WIND / LUNAR bars from day.factors (0–100). */
export function getHeroFactorBars(day) {
  const factors = day?.factors || {};
  const keys = [
    ['pressure', 'PRESSURE'],
    ['wind', 'WIND'],
    ['lunar', 'LUNAR'],
  ];
  return keys.map(([key, label]) => {
    const raw = Number(factors[key]);
    const value = Number.isFinite(raw) ? Math.max(0, Math.min(100, Math.round(raw))) : null;
    return { key, label, value, width: value == null ? 0 : value };
  });
}

export function formatSkyLabel(weatherType) {
  switch (weatherType) {
    case 'rainy':
      return 'RAIN';
    case 'cloudy':
      return 'CLOUDY';
    case 'partly_cloudy':
      return 'PARTLY CLOUDY';
    case 'sun':
      return 'CLEAR';
    default:
      return weatherType ? String(weatherType).replace(/_/g, ' ').toUpperCase() : '—';
  }
}

/** Short “why” line for BEST BET from existing factor bullets — no new scoring. */
export function buildHeroWhy(day) {
  const bullets = buildDetailScoreFactors(day);
  const positives = bullets.filter((b) => b.positive).map((b) => b.label);
  if (positives.length >= 2) return `${positives[0]}. ${positives[1]}.`;
  if (positives.length === 1) return `${positives[0]}.`;
  const first = bullets[0]?.label;
  return first ? `${first}.` : 'Conditions favor this stand for today’s sit.';
}

export function formatPressureInHg(hPa) {
  if (hPa == null || !Number.isFinite(Number(hPa))) return null;
  return `${(Number(hPa) * 0.02953).toFixed(2)}"`;
}

export function isWindOptimal(pick) {
  if (pick?.day?.wind_optimal_match != null) return Boolean(pick.day.wind_optimal_match);
  const winds = pick?.optimal_winds;
  const cardinal = pick?.day?.weather?.wind_cardinal
    || toForecastCardinal(pick?.day?.weather?.wind_deg);
  if (!Array.isArray(winds) || !winds.length || !cardinal) return false;
  return winds.includes(cardinal);
}

export function buildDetailFactorBullets(day, options) {
  return buildDetailScoreFactors(day, options);
}

export function deriveBestTimeHint(pick) {
  const tz = pick?.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const block = pick?.day?.solunar?.majors?.[0];
  if (!block?.start) return '—';
  try {
    const hour = new Date(block.start).toLocaleTimeString([], {
      hour: 'numeric',
      hour12: false,
      timeZone: tz,
    });
    const h = Number(hour);
    if (Number.isFinite(h)) {
      if (h < 11) return 'AM Hunt';
      if (h >= 15) return 'PM Hunt';
      return 'Midday';
    }
  } catch {
    /* fall through */
  }
  return '—';
}

export function formatSummaryDate(date = new Date()) {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  }).toUpperCase();
}

export function formatShortDayLabel(dateIso) {
  if (!dateIso) return '—';
  return formatLocalDate(dateIso, { weekday: 'short', month: 'short', day: 'numeric' });
}

export function pickEntityType(kind) {
  return kind === 'personal' ? 'geo-feature' : 'hunt-location';
}

export function resolveTodayDay(pick, days) {
  const fromFetch = pickTodayOptimalDay(days);
  const pickDay = pick?.day;

  if (fromFetch && fromFetch.score != null && dayHasWeather(fromFetch.weather)) {
    return fromFetch;
  }
  if (pickDay?.score != null && dayHasWeather(pickDay.weather)) {
    return pickDay;
  }
  if (fromFetch?.score != null) return fromFetch;
  return pickDay || fromFetch || null;
}

export function mergePickWithDay(pick, day) {
  if (!pick) return null;
  if (!day) return pick;
  return { ...pick, day };
}

/** API optimal_days payloads are score-sorted; display strips need calendar order. */
export function sortOptimalDaysChronologically(days) {
  const arr = Array.isArray(days) ? [...days] : [];
  arr.sort((a, b) => {
    const ay = toLocalYMD(a?.date || '');
    const by = toLocalYMD(b?.date || '');
    if (!ay && !by) return 0;
    if (!ay) return 1;
    if (!by) return -1;
    return ay < by ? -1 : ay > by ? 1 : 0;
  });
  return arr;
}

export function getPersonalStandMapPath(featureId) {
  if (featureId == null || featureId === '') return '/maps';
  return `/maps?featureId=${featureId}`;
}

/** Build enamel 8-day bar chart model from optimal_days (calendar order). */
export function buildEightDayChartModel(days) {
  const ordered = sortOptimalDaysChronologically(days).slice(0, 8);
  if (!ordered.length) {
    return { days: [], bestCaption: null, bestScore: null };
  }

  let best = ordered[0];
  ordered.forEach((d) => {
    if ((Number(d?.score) || -1) > (Number(best?.score) || -1)) best = d;
  });

  const chartDays = ordered.map((d) => {
    const scoreNum = Number(d?.score);
    const score = Number.isFinite(scoreNum) ? Math.max(0, Math.min(100, Math.round(scoreNum))) : null;
    let dow = '—';
    let day = '';
    if (d?.date) {
      try {
        const date = new Date(`${String(d.date).slice(0, 10)}T12:00:00`);
        dow = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
        day = String(date.getDate());
      } catch {
        /* ignore */
      }
    }
    return {
      date: d?.date || null,
      score,
      dow,
      day,
      height: score == null ? 0 : score,
      bandClass: getForecastScoreBandClass(score),
      isBest: Boolean(best?.date) && d?.date === best.date,
    };
  });

  let bestCaption = null;
  if (best?.date != null && Number.isFinite(Number(best.score))) {
    try {
      const date = new Date(`${String(best.date).slice(0, 10)}T12:00:00`);
      const label = date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }).toUpperCase();
      bestCaption = `BEST: ${label} · ${Math.round(Number(best.score))}`;
    } catch {
      bestCaption = `BEST · ${Math.round(Number(best.score))}`;
    }
  }

  return {
    days: chartDays,
    bestCaption,
    bestScore: Number.isFinite(Number(best?.score)) ? Math.round(Number(best.score)) : null,
  };
}
